import { useStore } from '@nanostores/react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  BandwidthBarChart,
  PageviewsAreaChart,
  VisitorsAreaChart,
  useBandwidthChart,
  usePageviewsChart,
  useVisitorsChart,
} from './analytics/Charts';
import { TopCountries, TopNotFound, TopPages, TopSources } from './analytics/TopLists';
import { SectionContainer } from '~/components/dialogs/settings/SectionContainer';
import { Alert } from '~/components/ui/Alert';
import { Skeleton } from '~/components/ui/Skeleton';
import * as Tooltip from '~/components/ui/Tooltip';
import { fetchNetlifyAnalytics } from '~/lib/.client/api/netlify';
import { chatHooks } from '~/lib/chat-hooks';
import { importStore } from '~/lib/projects/store';
import { projectStore } from '~/lib/stores/project';
import { themeIsDark } from '~/lib/stores/theme';
import { classNames } from '~/utils/classNames';

type Range = 'day' | 'week' | 'month';

function useNetlifyAnalyticsQuery(projectId: string | null | undefined, range: Range, enabled: boolean) {
  return useQuery({
    enabled: Boolean(projectId) && enabled,
    queryKey: ['netlify-analytics', projectId, range],
    queryFn: async () => {
      return await fetchNetlifyAnalytics(projectId as string, {
        range,
        metrics: 'bandwidth,pageviews,visitors,pages,sources,countries,not_found',
        limit: 10,
      });
    },
    staleTime: 5 * 60 * 1000,
  });
}

const queryClient = new QueryClient();

function AnalyticsContent() {
  const projectId = useStore(projectStore.projectId);
  const [range, setRange] = useState<Range>('month');
  const { deploymentInfo } = useStore(importStore);
  const isBoltHosted = Boolean(deploymentInfo && deploymentInfo.kind === 'static-hosting');
  const { data, isLoading, error } = useNetlifyAnalyticsQuery(projectId, range, isBoltHosted);

  const pageviewsChart = usePageviewsChart(data?.pageviews);

  const visitorsChart = useVisitorsChart(data?.visitors);

  const bandwidthChart = useBandwidthChart(data?.bandwidth);

  const topPages = useMemo(() => {
    const rows = (data?.pages ?? []).map((p) => ({
      url: p.url || p.path || '',
      count: p.count,
    }));
    return rows;
  }, [data]);

  const topSources = useMemo(() => {
    const rows = (data?.sources ?? []).map((s: any) => {
      const src: string = s.source;
      const label = s.source || 'Direct';
      const url = src ? (/^https?:\/\//i.test(src) ? src : `https://${src}`) : '';

      return { label, url, count: (s.count as number) ?? 0 };
    });
    return rows;
  }, [data]);

  const topCountries = useMemo(() => {
    const rows = (data?.countries ?? []).map((c) => ({
      code: (c.country_code || c.code || '').toUpperCase(),
      name: c.country || (c.country_code || c.code || '').toUpperCase(),
      count: c.count,
    }));
    return rows;
  }, [data]);

  const topNotFound = useMemo(() => {
    const rows = (data?.not_found ?? []).map((n) => ({
      url: n.url || n.path || n.resource || '',
      count: n.count,
    }));
    return rows;
  }, [data]);

  const headerDates = useMemo(() => {
    if (!data) {
      return null;
    }

    const from = new Date(data.from);
    const to = new Date(data.to);
    const sameYear = from.getFullYear() === to.getFullYear();

    const fmt: Intl.DateTimeFormatOptions = sameYear
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' };

    return `${from.toLocaleDateString(undefined, fmt)} to ${to.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }, [data]);

  const gridStroke = themeIsDark() ? '#ffffff15' : '#00000015';

  const isDeploying = useStore(chatHooks.isDeploying);

  const requiresBoltHosting =
    (!deploymentInfo && !isDeploying) ||
    (!!deploymentInfo && !isBoltHosted) ||
    (error && (error as Error)?.message?.includes('Only available on Bolt Hosted projects'));

  if (requiresBoltHosting) {
    return (
      <div className="flex flex-col gap-3 items-center h-full justify-center">
        <div className="size-16 flex items-center justify-center rounded-full bg-bolt-ds-surfaceTwo">
          <div
            className={classNames(
              'text-bolt-ds-text-secondary size-8 text-bolt-ds-textTertiary',
              'i-heroicons:shield-check',
            )}
          />
        </div>
        <div>Only available on Bolt Hosted projects</div>
        <div className="text-bolt-ds-text-secondary text-sm text-center max-w-100">
          Publish with Bolt Hosting to access analytics for this project.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <SectionContainer title="Analytics" className="mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-bolt-ds-textSecondary">View usage metrics for this project.</div>
          <div className="flex items-center gap-1.5 text-xs">
            <button
              className={`px-2 py-1 rounded border ${range === 'day' ? 'bg-bolt-ds-surfaceThree' : 'bg-bolt-ds-surfaceTwo'} border-bolt-ds-borderOutline`}
              onClick={() => setRange('day')}
            >
              Day
            </button>
            <button
              className={`px-2 py-1 rounded border ${range === 'week' ? 'bg-bolt-ds-surfaceThree' : 'bg-bolt-ds-surfaceTwo'} border-bolt-ds-borderOutline`}
              onClick={() => setRange('week')}
            >
              Week
            </button>
            <button
              className={`px-2 py-1 rounded border ${range === 'month' ? 'bg-bolt-ds-surfaceThree' : 'bg-bolt-ds-surfaceTwo'} border-bolt-ds-borderOutline`}
              onClick={() => setRange('month')}
            >
              Month
            </button>
          </div>
        </div>
      </SectionContainer>

      {isLoading && (
        <div className="flex flex-col gap-6">
          <Skeleton className="h-[260px] w-full rounded-lg" />
          <Skeleton className="h-[260px] w-full rounded-lg" />
          <Skeleton className="h-[260px] w-full rounded-lg" />
        </div>
      )}

      {error && (
        <Alert type="danger" title="Failed to load analytics">
          {(error as Error)?.message || 'Failed to load analytics'}
        </Alert>
      )}

      {!isLoading && !error && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2 bg-bolt-ds-surfaceTwo border border-bolt-ds-borderDivider rounded-lg p-4">
            <div className="flex flex-col gap-1 border-b border-bolt-ds-borderDivider pb-2">
              <div className="flex items-center gap-1 text-sm font-semibold text-bolt-ds-textPrimary">
                Unique Visitors
                <Tooltip.Provider>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <span className="i-ph:info-fill" />
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content side="top" align="center" sideOffset={6}>
                        Different IP addresses engaging with your project over the selected period.
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </div>
              {headerDates ? <div className="text-xs text-bolt-ds-textTertiary">{headerDates}</div> : null}
            </div>
            <VisitorsAreaChart data={visitorsChart.rows} gridStroke={gridStroke} />
          </div>
          <div className="flex flex-col gap-2 bg-bolt-ds-surfaceTwo border border-bolt-ds-borderDivider rounded-lg p-4">
            <div className="flex flex-col gap-1 border-b border-bolt-ds-borderDivider pb-2">
              <div className="flex items-center gap-1 text-sm font-semibold text-bolt-ds-textPrimary">
                Pageviews
                <Tooltip.Provider>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <span className="i-ph:info-fill" />
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content side="top" align="center" sideOffset={6}>
                        Instances of your project’s pages being served.
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </div>
              {headerDates ? <div className="text-xs text-bolt-ds-textTertiary">{headerDates}</div> : null}
            </div>
            <PageviewsAreaChart data={pageviewsChart.rows} gridStroke={gridStroke} />
          </div>

          <div className="flex flex-col gap-2 bg-bolt-ds-surfaceTwo border border-bolt-ds-borderDivider rounded-lg p-4">
            <div className="flex items-center justify-between border-b border-bolt-ds-borderDivider pb-2">
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1 text-sm font-semibold text-bolt-ds-textPrimary">
                  Bandwidth
                  <Tooltip.Provider>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <span className="i-ph:info-fill" />
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content side="top" align="center" sideOffset={6}>
                          Amount of data served by our CDN for your project.
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </Tooltip.Provider>
                </div>
                {headerDates ? <div className="text-xs text-bolt-ds-textTertiary">{headerDates}</div> : null}
              </div>
            </div>
            <BandwidthBarChart data={bandwidthChart.rows} gridStroke={gridStroke} />
          </div>

          <TopPages rows={topPages} />

          <TopSources rows={topSources} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TopCountries rows={topCountries} />

            <TopNotFound rows={topNotFound} />
          </div>
        </div>
      )}
    </div>
  );
}

export const Analytics = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AnalyticsContent />
    </QueryClientProvider>
  );
};
